// src/services/portfolio.js
const COVALENT_KEY = process.env.REACT_APP_COVALENT_KEY;
const BIRDEYE_KEY  = process.env.REACT_APP_BIRDEYE_KEY;

// Map your "chain" string -> Covalent chain_id
const COVALENT_CHAIN_IDS = {
  ethereum: 1,
  eth: 1,
  arbitrum: 42161,
  arb: 42161,
  optimism: 10,
  op: 10,
  polygon: 137,
  matic: 137,
  bsc: 56,
  binance: 56,
  base: 8453,
  avalanche: 43114,
  avax: 43114,
  fantom: 250
};

const usd = (n) => {
  const v = Number(n);
  if (!isFinite(v)) return "$-";
  if (v >= 1000) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const digits = v < 0.01 ? 8 : v < 1 ? 6 : 4;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: digits })}`;
};

function topN(arr = [], n = 3) {
  return [...arr].sort((a, b) => (b.usdValue || 0) - (a.usdValue || 0)).slice(0, n);
}

/** Fetch EVM balances via Covalent (free tier) and compute USD values */
export async function fetchEvmPortfolio(chain, address) {
  const chainId = COVALENT_CHAIN_IDS[String(chain).toLowerCase()];
  if (!chainId) throw new Error(`Unsupported EVM chain: ${chain}`);

  const url = `https://api.covalenthq.com/v1/${chainId}/address/${address}/balances_v2/?nft=false&no-nft-fetch=true&key=${encodeURIComponent(COVALENT_KEY || "")}`;

  const r = await fetch(url);
  if (!r.ok) throw new Error(`Covalent ${chain}:${address} ${r.status}`);
  const j = await r.json();

  const items = j?.data?.items || [];
  const tokens = items
    .filter(it => it?.type !== "nft" && it?.contract_decimals != null)
    .map(it => {
      const dec = Number(it.contract_decimals) || 0;
      const bal = Number(it.balance || 0) / Math.pow(10, dec);
      const price = Number(it.quote_rate || 0);
      return {
        symbol: it.contract_ticker_symbol || "TKN",
        name: it.contract_name || it.contract_ticker_symbol || "Token",
        logo: it.logo_url || "",
        amount: bal,
        price,
        usdValue: bal * price,
        address: it.contract_address
      };
    });

  const totalUsd = tokens.reduce((s, t) => s + (t.usdValue || 0), 0);
  return { totalUsd, tokens };
}

/** Fetch Solana balances via Birdeye (free tier) and compute USD values */
export async function fetchSolPortfolio(address) {
  if (!BIRDEYE_KEY) throw new Error("Missing REACT_APP_BIRDEYE_KEY");

  // token list for wallet
  const url = `https://public-api.birdeye.so/v1/wallet/token_list?wallet=${encodeURIComponent(address)}`;
  const r = await fetch(url, {
    headers: {
      "X-API-KEY": BIRDEYE_KEY,
      "x-chain": "solana",
      "accept": "application/json"
    }
  });
  if (!r.ok) throw new Error(`Birdeye wallet ${r.status}`);
  const j = await r.json();
  const tokensRaw = j?.data?.tokens || [];

  // Birdeye token_list returns { address, uiAmount, ... } — we’ll fetch prices in batch
  const mints = tokensRaw.map(t => t.address).filter(Boolean);
  let prices = {};
  if (mints.length) {
    // batch price query
    const priceUrl = `https://public-api.birdeye.so/v1/price/multi?list_address=${encodeURIComponent(mints.join(","))}`;
    const pr = await fetch(priceUrl, {
      headers: {
        "X-API-KEY": BIRDEYE_KEY,
        "x-chain": "solana",
        "accept": "application/json"
      }
    });
    if (pr.ok) {
      const pj = await pr.json();
      prices = pj?.data || {};
    }
  }

  const tokens = tokensRaw.map(t => {
    const price = Number(prices?.[t.address]?.value || 0);
    const amt = Number(t.uiAmount || 0);
    return {
      symbol: t?.symbol || t?.address?.slice(0,4) + "…",
      name: t?.name || t?.address,
      logo: t?.logoURI || "",
      amount: amt,
      price,
      usdValue: amt * price,
      address: t.address
    };
  });

  const totalUsd = tokens.reduce((s, x) => s + (x.usdValue || 0), 0);
  return { totalUsd, tokens };
}

/** Unified fetch for one whale */
export async function fetchWhaleSnapshot(whale) {
  const chain = String(whale.chain || "").toLowerCase();
  if (chain === "solana" || chain === "sol") {
    return await fetchSolPortfolio(whale.address);
  } else {
    return await fetchEvmPortfolio(chain, whale.address);
  }
}
